import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LuChevronDown, LuChevronLeft, LuChevronRight, LuX } from "react-icons/lu";
import { SITELINKS } from "../app-routes/links";
import { HoneConnectLogo } from "../assets/img";
import { childHref, NAV_ITEMS, NavChild, NavItem } from "./sidenav/navItems";

// square app icon (also the favicon), used when the sidebar is collapsed
const APP_ICON = `${process.env.PUBLIC_URL}/HoneConnect.png`;

type SideNavProps = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
};

const trimSlash = (p: string) => p.replace(/\/+$/, "") || "/";

const SideNavAdmin = ({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
}: SideNavProps) => {
  const location = useLocation();
  const path = trimSlash(location.pathname);
  const currentStatus = new URLSearchParams(location.search).get("status") ?? "";

  const isChildActive = (c: NavChild) =>
    path === trimSlash(c.to) && (c.status === undefined || c.status === currentStatus);
  const isItemActive = (item: NavItem) =>
    item.to ? path === trimSlash(item.to) : !!item.children?.some(isChildActive);

  // Which groups are expanded; the group for the current page opens automatically
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const active = NAV_ITEMS.find((i) => i.children && isItemActive(i));
    if (active) setOpenGroups((g) => ({ ...g, [active.label]: true }));
    onCloseMobile(); // close the phone drawer after navigating
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // The phone drawer always shows full labels
  const iconOnly = collapsed && !mobileOpen;

  return (
    <>
      {/* phone backdrop */}
      <div
        aria-hidden
        onClick={onCloseMobile}
        className={`fixed inset-0 z-30 bg-black/30 transition-opacity md:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      <aside
        id="admin-sidenav"
        aria-label="Main navigation"
        className={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r border-hcBorder bg-white transition-[width,transform] duration-200 ${
          iconOnly ? "md:w-[76px]" : "md:w-64"
        } ${
          mobileOpen ? "translate-x-0 shadow-pop" : "-translate-x-full"
        } md:translate-x-0 md:shadow-none`}
      >
        {/* ---------- brand + collapse ---------- */}
        <div
          className={`relative flex h-20 shrink-0 items-center border-b border-hcBorder ${
            iconOnly ? "justify-center" : "justify-between px-6"
          }`}
        >
          <Link
            to={SITELINKS.DASHBOARD}
            aria-label="HoneConnect admin home"
            className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40"
          >
            {iconOnly ? (
              <img src={APP_ICON} alt="" className="h-10 w-10 rounded-xl" />
            ) : (
              <img src={HoneConnectLogo} alt="" className="-ml-[5px] h-12 w-auto" />
            )}
          </Link>

          {/* phones: close the drawer */}
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Close menu"
            className="-mr-2 flex h-10 w-10 items-center justify-center rounded-full text-honeConnectGray transition-colors hover:bg-hcBg hover:text-honeConnectBlack focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40 md:hidden"
          >
            <LuX className="h-5 w-5" aria-hidden />
          </button>

          {/* tablet/desktop: collapse toggle sitting on the sidebar edge */}
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-controls="admin-sidenav"
            aria-expanded={!collapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="absolute -right-3.5 top-1/2 z-10 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-hcBorder bg-white text-honeConnectGray shadow-sm transition-colors hover:border-honeConnectPurple hover:text-honeConnectPurple focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40 md:flex"
          >
            {collapsed ? (
              <LuChevronRight className="h-4 w-4" aria-hidden />
            ) : (
              <LuChevronLeft className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>

        <nav className={`flex-1 py-5 ${iconOnly ? "overflow-visible" : "overflow-y-auto"}`}>
          {!iconOnly && (
            <p className="px-6 pb-2 text-[11px] font-semibold uppercase tracking-wider text-hcMuted">
              Menu
            </p>
          )}

          <ul className="space-y-1 px-3">
            {NAV_ITEMS.map((item) => {
              const active = isItemActive(item);
              const Icon = item.icon;
              const groupOpen = !!openGroups[item.label];
              const rowClass = `relative flex h-11 w-full items-center rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40 ${
                iconOnly ? "justify-center" : "gap-3 px-3"
              } ${
                active
                  ? "bg-hc_F4EBFA text-honeConnectPurple"
                  : "text-honeConnectGray hover:bg-hcBg hover:text-honeConnectBlack"
              }`;

              return (
                <li key={item.label} className="group/item relative">
                  {/* ---------- top-level row ---------- */}
                  {item.to ? (
                    <Link
                      to={item.to}
                      aria-current={active ? "page" : undefined}
                      aria-label={iconOnly ? item.label : undefined}
                      className={rowClass}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden />
                      {!iconOnly && <span className="truncate">{item.label}</span>}
                    </Link>
                  ) : iconOnly ? (
                    // collapsed group: icon opens its first page; the flyout has the rest
                    <Link
                      to={childHref(item.children![0])}
                      aria-label={item.label}
                      className={rowClass}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroups((g) => ({ ...g, [item.label]: !g[item.label] }))
                      }
                      aria-expanded={groupOpen}
                      aria-controls={`nav-group-${item.label}`}
                      className={rowClass}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden />
                      <span className="flex-1 truncate text-left">{item.label}</span>
                      <LuChevronDown
                        aria-hidden
                        className={`h-4 w-4 shrink-0 transition-transform duration-150 ${
                          groupOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}

                  {/* ---------- expanded submenu ---------- */}
                  {!iconOnly && item.children && groupOpen && (
                    <ul
                      id={`nav-group-${item.label}`}
                      className="mb-1 ml-[22px] mt-1 space-y-0.5 border-l border-hcBorder pl-3"
                    >
                      {item.children.map((c) => {
                        const childActive = isChildActive(c);
                        return (
                          <li key={c.label}>
                            <Link
                              to={childHref(c)}
                              aria-current={childActive ? "page" : undefined}
                              className={`relative flex h-9 items-center rounded-md px-3 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40 ${
                                childActive
                                  ? "font-medium text-honeConnectPurple"
                                  : "text-honeConnectGray hover:bg-hcBg hover:text-honeConnectBlack"
                              }`}
                            >
                              {childActive && (
                                <span
                                  aria-hidden
                                  className="absolute -left-[13px] h-5 w-0.5 rounded-full bg-honeConnectPurple"
                                />
                              )}
                              {c.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* ---------- collapsed flyout (hover or keyboard focus) ---------- */}
                  {iconOnly && (
                    <div className="invisible absolute left-full top-0 z-50 pl-3 opacity-0 transition-opacity duration-100 group-focus-within/item:visible group-focus-within/item:opacity-100 group-hover/item:visible group-hover/item:opacity-100">
                      <div className="min-w-[184px] overflow-hidden rounded-xl border border-hcBorder bg-white py-1.5 shadow-pop">
                        <p
                          className={`px-4 py-2 text-sm font-semibold text-honeConnectBlack ${
                            item.children ? "border-b border-hcBorder" : ""
                          }`}
                        >
                          {item.label}
                        </p>
                        {item.children?.map((c) => {
                          const childActive = isChildActive(c);
                          return (
                            <Link
                              key={c.label}
                              to={childHref(c)}
                              aria-current={childActive ? "page" : undefined}
                              className={`block px-4 py-2 text-sm transition-colors focus:bg-hcBg focus:outline-none ${
                                childActive
                                  ? "bg-hc_F4EBFA font-medium text-honeConnectPurple"
                                  : "text-honeConnectGray hover:bg-hcBg hover:text-honeConnectBlack"
                              }`}
                            >
                              {c.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

      </aside>
    </>
  );
};

export default SideNavAdmin;
